import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `uploads:` scope (F18), following the exact
 * `documents:` precedent (`apps/web/src/routes/documents/documents.i18n.ts`)
 * — NOT a second i18n runtime (E18/§13.3 ban), just physically scoped here
 * because `packages/i18n/**` is outside this task's ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/uploads.json`.
 */
const en = {
  'modal.title': 'Upload sources',
  'dropzone.cta': 'Drop files here, or browse',
  'dropzone.browse': 'Browse files',
  'dropzone.hint': 'PDF · DOC · DOCX · TXT · max 25 MB each',
  'queue.title': 'Queue',
  'queue.empty': 'No files added yet.',
  'status.validating': 'Checking…',
  'status.queued': 'Waiting…',
  'status.uploading': 'Uploading…',
  'status.uploaded': 'Uploaded',
  'status.ingesting': 'Processing…',
  'status.ready': 'Ready',
  'status.failed': 'Failed',
  'action.retry': 'Retry',
  'action.cancel': 'Cancel',
  'action.remove': 'Remove',
  'action.close': 'Close',
  'action.done': 'Done',
  'summary.allReady': '{count, plural, one {# source ready} other {# sources ready}}',
  'summary.inProgress': '{count, plural, one {# file in progress} other {# files in progress}}',
  'error.mimeType.unsupported': 'That file type isn’t supported here — only PDF, DOC, DOCX, and TXT files can be uploaded.',
  'error.size.tooLarge': 'That file is too large — the limit is 25 MB per file.',
  'error.network': 'The upload was interrupted — check your connection and retry.',
  'error.parse.generic': 'We couldn’t read this file — it may be corrupted, password-protected, or scanned/image-only.',
  'toast.uploaded.action': 'Uploaded',
} as const;

const ro: Record<keyof typeof en, string> = {
  'modal.title': 'Încarcă surse',
  'dropzone.cta': 'Trage fișiere aici, sau răsfoiește',
  'dropzone.browse': 'Răsfoiește fișiere',
  'dropzone.hint': 'PDF · DOC · DOCX · TXT · max 25 MB fiecare',
  'queue.title': 'Coadă',
  'queue.empty': 'Niciun fișier adăugat încă.',
  'status.validating': 'Se verifică…',
  'status.queued': 'În așteptare…',
  'status.uploading': 'Se încarcă…',
  'status.uploaded': 'Încărcat',
  'status.ingesting': 'Se procesează…',
  'status.ready': 'Gata',
  'status.failed': 'Eșuat',
  'action.retry': 'Reîncearcă',
  'action.cancel': 'Anulează',
  'action.remove': 'Elimină',
  'action.close': 'Închide',
  'action.done': 'Gata',
  'summary.allReady': '{count, plural, one {# sursă gata} few {# surse gata} other {# de surse gata}}',
  'summary.inProgress': '{count, plural, one {# fișier în curs} few {# fișiere în curs} other {# de fișiere în curs}}',
  'error.mimeType.unsupported': 'Acest tip de fișier nu este acceptat aici — se pot încărca doar fișiere PDF, DOC, DOCX și TXT.',
  'error.size.tooLarge': 'Acest fișier este prea mare — limita este 25 MB per fișier.',
  'error.network': 'Încărcarea a fost întreruptă — verifică conexiunea și reîncearcă.',
  'error.parse.generic': 'Nu am putut citi acest fișier — poate fi corupt, protejat prin parolă sau scanat/doar imagine.',
  'toast.uploaded.action': 'Încărcat',
};

export const UPLOADS_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type UploadsCatalogKey = keyof typeof en;
